import { getClientFunctions } from "@/lib/firebase-client";
import { httpsCallable } from "firebase/functions";

// ── Preview types ────────────────────────────────────────────────────────────

export interface DowngradePreviewMember {
  uid: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  role: string;
  isActive: boolean;
}

export interface DowngradePreviewRecipe {
  id: string;
  title: string;
  imageUrl: string;
  isActive: boolean;
  createdAt: unknown;
}

export interface DowngradePreviewBox {
  id: string;
  title: string;
  imageUrl: string;
  activeRecipeCount: number;
  totalRecipeCount: number;
  activeMemberCount: number;
  totalMemberCount: number;
  recipesOverLimit: number;
  membersOverLimit: number;
  createdAt: unknown;
  members: DowngradePreviewMember[];
  recipes: DowngradePreviewRecipe[];
}

export interface DowngradePreview {
  newLimits: {
    maxRecipeBoxes: number | null;
    maxRecipesPerBox: number | null;
    maxMembersPerBox: number | null;
  };
  boxes: DowngradePreviewBox[];
  currentActiveBoxCount: number;
  mustDeactivateBoxes: number;
}

// ── getDowngradePreview ──────────────────────────────────────────────────────

export async function getDowngradePreview(): Promise<DowngradePreview> {
  const functions = getClientFunctions();
  if (!functions) throw new Error("Firebase functions not available");

  const callable = httpsCallable<Record<string, never>, DowngradePreview>(
    functions,
    "getDowngradePreview",
  );

  const response = await callable({});
  return response.data;
}

// ── initiateDowngrade ────────────────────────────────────────────────────────

export interface InitiateDowngradeInput {
  activeBoxIds: string[];
  activeRecipeIds: string[];
  activeMemberUids: string[];
}

export interface InitiateDowngradeResult {
  success: boolean;
  message?: string;
}

export async function initiateDowngrade(
  input: InitiateDowngradeInput,
): Promise<InitiateDowngradeResult> {
  const functions = getClientFunctions();
  if (!functions) throw new Error("Firebase functions not available");

  const callable = httpsCallable<InitiateDowngradeInput, InitiateDowngradeResult>(
    functions,
    "initiateDowngrade",
  );

  const response = await callable(input);
  return response.data;
}

// ── setItemActive ────────────────────────────────────────────────────────────

export type SetItemActiveItemType = "recipeBox" | "recipe" | "member";

export interface SetItemActiveInput {
  itemType: SetItemActiveItemType;
  itemId: string;
  isActive: boolean;
  /** Required when itemType is "recipe" or "member" */
  recipeBoxId?: string;
}

export interface SetItemActiveResult {
  success: boolean;
  message?: string;
}

export async function setItemActive(
  input: SetItemActiveInput,
): Promise<SetItemActiveResult> {
  const functions = getClientFunctions();
  if (!functions) throw new Error("Firebase functions not available");

  const callable = httpsCallable<SetItemActiveInput, SetItemActiveResult>(
    functions,
    "setItemActive",
  );

  const response = await callable(input);
  return response.data;
}
